import { TabSelectItem, VerticalTabSelect } from "~/components/TabSelect";
import { Asset, Token } from "~/modules/assets/types";
import { TokenHoldersFetcher } from "../TokenHoldersFetcher";
import {
    ApiQuery,
    FilterOperators,
    SortDirections,
} from "~/modules/filters/types";
import { TokenHoldersColumns } from "~/modules/token_holders/types";
import { IconTypes } from "~/components/Icon/types";
import { TokenHolderRowData } from "../TokenHoldersTable/types";
import {
    TokenHoldersEmptyState,
    isConfidentialNetwork,
} from "./TokenHoldersEmptyState";
import { TokenHoldersLoadingState } from "./TokenHoldersLoadingState";
import * as Analytics from "../../analytics";
import { useUser } from "~/modules/user/hooks/useUser";
import { TokenHoldersLoginCTA } from "./TokenHoldersLoginCTA";
import { TokenHoldersCard } from "./TokenHoldersCard";
import { TokenHoldersDisplay } from "./TokenHoldersDisplay";

// // // //

type TokenHoldersTab = TabSelectItem<{
    token: Token | null;
}>;

function getTabs(props: {
    asset: Asset;
    validTokens: Token[];
}): Array<TokenHoldersTab> {
    const { asset, validTokens } = props;

    // Build items for TabSelect
    const tabs: Array<TokenHoldersTab> = validTokens.map((t) => {
        return {
            key: String(t.id),
            content: {
                label: t.network?.name ?? t.name,
                icon: t.network?.icon_url,
            },
            data: {
                token: t,
            },
        };
    });

    // Add "All Tokens" tab if there is more than one tab
    if (tabs.length > 1) {
        const ALL_TOKENS_TAB: TokenHoldersTab = {
            key: "all",
            content: {
                label: "All Networks",
                icon: IconTypes.CircleNodes,
            },
            data: {
                token: null,
            },
        };

        tabs.unshift(ALL_TOKENS_TAB);
    }

    return tabs;
}

// // // //

export function AssetTokenHoldersLayout(props: {
    asset: Asset;
    rows: Array<TokenHolderRowData>;
    validTokens: Token[];
}) {
    const { asset, rows, validTokens } = props;

    const tabs = getTabs({ asset, validTokens });

    return (
        <TokenHoldersCard title="Holders" token_count={validTokens.length}>
            <VerticalTabSelect
                tabs={tabs}
                name="asset-token-holders-tabs"
                onTabChange={({ prev, next }) => {
                    // Track tab change in analytics
                    Analytics.trackTokenHoldersTabChange({
                        prev: prev.content.label,
                        next: next.content.label,
                        asset_id: asset.id,
                        asset_ticker: asset.ticker ?? "",
                    });
                }}
            >
                {(tab) => {
                    if (!tab) {
                        return null;
                    }
                    // Filter the rows based on which tab is selected
                    const filteredRows =
                        tab.data.token === null
                            ? rows
                            : rows.filter(
                                  (r) =>
                                      String(r.holder.token_id) ===
                                      String((tab.data.token as Token).id),
                              );

                    // Display empty state if there are no results for the selected tab
                    if (filteredRows.length === 0) {
                        const network = tab.data.token?.network;
                        return (
                            <TokenHoldersEmptyState
                                networkSlug={network?.slug}
                                networkName={network?.name}
                            />
                        );
                    }

                    // Get label for TokenHoldersDisplay
                    const label =
                        tab.key === "all"
                            ? `${asset.name} Holders`
                            : `${filteredRows[0]?.token.name} Holders`;

                    // Get icon for TokenHoldersDisplay
                    const icon_url =
                        tab.key === "all"
                            ? asset.icon_url
                            : filteredRows[0]?.token.network?.icon_url;

                    // Get token for TokenHoldersDisplay - only available when a
                    // single network is selected (the "all" tab spans multiple
                    // tokens, each with their own contract address)
                    const token =
                        tab.key === "all"
                            ? null
                            : (tab.data.token ??
                              filteredRows[0]?.token ??
                              null);

                    // Render TokenHoldersDisplay component (table + chart)
                    return (
                        <TokenHoldersDisplay
                            content={{
                                label: label,
                                icon_url: icon_url,
                                token: token,
                            }}
                            rows={filteredRows}
                        />
                    );
                }}
            </VerticalTabSelect>
        </TokenHoldersCard>
    );
}

// // // //

export function AssetTokenHolders(props: { asset: Asset }) {
    const { asset } = props;

    const { isLoggedIn } = useUser();

    // Collect valid tokens
    const validTokens = asset.tokens.filter((t) => {
        const holders = t.holding_addresses_count?.val || 0;
        return holders > 1;
    });

    // Check if all tokens are on confidential networks
    const allTokensConfidential =
        asset.tokens.length > 0 &&
        asset.tokens.every((t) => isConfidentialNetwork(t.network?.slug));

    // If all tokens are on confidential networks, show the notice
    if (allTokensConfidential) {
        const firstToken = asset.tokens[0];
        return (
            <TokenHoldersCard title="Holders" token_count={validTokens.length}>
                <TokenHoldersEmptyState
                    networkSlug={firstToken?.network?.slug}
                    networkName={firstToken?.network?.name}
                />
            </TokenHoldersCard>
        );
    }

    // Return null if validTokens.length === 0
    if (validTokens.length === 0) {
        return null;
    }

    // If user isn't logged in -> render CTA instead
    if (!isLoggedIn) {
        return (
            <TokenHoldersCard title="Holders" token_count={0}>
                <TokenHoldersLoginCTA asset={asset} />
            </TokenHoldersCard>
        );
    }

    // Build query for TokenHolders API
    const query: ApiQuery = {
        filter: {
            operator: FilterOperators.Or,
            filters: validTokens.map((t) => {
                return {
                    operator: FilterOperators.Equals,
                    value: t.id,
                    field: TokenHoldersColumns.tokenID,
                };
            }),
        },
        sort: {
            direction: SortDirections.desc,
            field: TokenHoldersColumns.holdingTokenValueUSD,
        },
        pagination: {
            page: 1,
            perPage: 1500,
        },
    };

    return (
        <TokenHoldersFetcher query={query}>
            {({ loading, data }) => {
                // Display loader if no results have been fetched yet
                if (loading) {
                    return (
                        <TokenHoldersCard
                            title="Holders"
                            token_count={validTokens.length}
                        >
                            <TokenHoldersLoadingState />
                        </TokenHoldersCard>
                    );
                }

                // Build TokenHolderRowData objects from API response + Asset.tokens data
                const allRows: TokenHolderRowData[] = data.results
                    .map((holder, idx) => {
                        const tkn = asset.tokens.find((t) => {
                            return String(t.id) === String(holder.token_id);
                        });

                        if (!tkn) {
                            return null;
                        }

                        return {
                            key: `${holder.address || "NO-ADDR"}-${tkn.id}-${holder.holding_token_value}`,
                            holder,
                            token: tkn,
                            rank:
                                (data.pagination.page - 1) *
                                    data.pagination.perPage +
                                idx +
                                1,
                        };
                    })
                    .filter((each) => each !== null) as TokenHolderRowData[];

                // The API can return exact duplicate holder rows. Drop them so
                // row keys stay unique - duplicate keys leave stale rows in the
                // table when paginating.
                const seenKeys = new Set<string>();
                const rows = allRows.filter((r) => {
                    if (seenKeys.has(r.key)) {
                        return false;
                    }
                    seenKeys.add(r.key);
                    return true;
                });

                // Only display tokens that have holders data from the API
                const filteredValidTokens = validTokens.filter((t) => {
                    if (rows.length === 0) {
                        return true;
                    }
                    return (
                        rows.find((r) => {
                            return String(t.id) === String(r.holder.token_id);
                        }) !== undefined
                    );
                });

                return (
                    <AssetTokenHoldersLayout
                        asset={asset}
                        validTokens={filteredValidTokens}
                        rows={rows}
                    />
                );
            }}
        </TokenHoldersFetcher>
    );
}
